import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { FacultyStudentProfile, FacultyStudentProfileSkill } from '../types';
import { portalApi } from '../utils/api';
import MathMarkdown from '@/components/MathMarkdown';

function readinessLabel(value: string) {
  if (value === 'ready') return 'Ready';
  if (value === 'ready_with_review') return 'Ready with Review';
  if (value === 'not_ready') return 'Not Ready';
  return 'Pending';
}

function blameSourceLabel(
  value: FacultyStudentProfileSkill['evidence_questions'][number]['blame_source']
) {
  if (value === null) return 'Demonstrated';
  if (value === 'misconception') return 'Misconception';
  if (value === 'followup') return 'Follow-up';
  if (value === 'skip_reason') return 'Skipped';
  if (value === 'canonical_form') return 'Answer Pattern';
  return 'Evidence';
}

export default function FacultyStudentProfilePage() {
  const navigate = useNavigate();
  const { sectionId, studentId } = useParams<{ sectionId: string; studentId: string }>();
  const [searchParams] = useSearchParams();
  const educatorUserId = searchParams.get('educator_user_id') || undefined;
  const assessmentId = searchParams.get('assessment_id') || undefined;
  const queryParams = new URLSearchParams();
  if (educatorUserId) queryParams.set('educator_user_id', educatorUserId);
  if (assessmentId) queryParams.set('assessment_id', assessmentId);
  const educatorQuerySuffix = queryParams.toString() ? `?${queryParams.toString()}` : '';

  const { data: profile, isLoading, error } = useQuery<FacultyStudentProfile>({
    queryKey: ['portal', 'faculty', 'student-profile', sectionId, studentId, educatorUserId, assessmentId],
    queryFn: () => portalApi.getFacultyStudentProfile(sectionId!, studentId!, educatorUserId, assessmentId),
    enabled: !!sectionId && !!studentId,
  });

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error ? 'Failed to load student profile' : 'Student profile not found'}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(`/educator-portal/classes/${sectionId}${educatorQuerySuffix}`)}>
          Back to Assessment
        </Button>
      </Box>
    );
  }

  const studentDisplay = `${profile.student.first_name || ''} ${profile.student.last_name || ''}`.trim() || profile.student.email || 'Unnamed student';

  const renderSkillAccordions = (skills: FacultyStudentProfileSkill[]) => {
    if (skills.length === 0) {
      return <Typography color="text.secondary" variant="body2">No skills in this section.</Typography>;
    }

    return (
      <List disablePadding>
        {skills.map((skill, index) => (
          <Accordion key={skill.skill_id} disableGutters defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ width: '100%' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle2" fontWeight={700}>{skill.skill_name}</Typography>
                    {skill.resource_reviewed ? (
                      <Tooltip title="Student has visited the resources for this skill.">
                        <Chip label="Reviewed" size="small" color="success" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Student has not visited the resources for this skill yet.">
                        <Chip label="Not Reviewed" size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {(!Array.isArray(skill.evidence_questions) || skill.evidence_questions.length === 0) ? (
                <Typography color="text.secondary" variant="body2">
                  No supporting questions recorded for this skill yet.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {skill.evidence_questions.map((item) => (
                    <Paper key={`${skill.skill_id}-${item.question_id}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" fontWeight={700}>{`Question ${item.question_number}`}</Typography>
                          <Chip label={blameSourceLabel(item.blame_source)} size="small" variant="outlined" />
                        </Stack>
                        <MathMarkdown sx={{ '& p': { m: 0 } }}>
                          {item.question_text}
                        </MathMarkdown>

                        <Box sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                          {item.student_answer && (
                            <MathMarkdown sx={{ '& p': { m: 0 } }}>
                                {`Student answer: $$${item.student_answer}$$`}
                            </MathMarkdown>
                          )}
                          {!item.student_answer && skill.mastery_level !== 'mastered' && (
                            <Typography variant="body2">
                              Student answer: No student answer recorded
                            </Typography>
                          )}
                          {item.correct_answer ? (
                            <MathMarkdown sx={{ '& p': { m: 0 } }}>
                              {`Correct answer: ${item.correct_answer}`}
                            </MathMarkdown>
                          ) : (
                            <Typography variant="body2">
                              Correct answer: No correct answer available
                            </Typography>
                          )}
                        </Box>

                        {item.blame_source === 'misconception' && (
                          <Typography variant="body2" color="text.secondary">
                            Misconception: {item.misconception_description || 'No misconception description available'}
                          </Typography>
                        )}

                        {item.blame_source === 'followup' && (
                          <Box>
                            <Typography variant="body2" fontWeight={700}>Follow-up question</Typography>
                            {item.followup_question_text ? (
                              <MathMarkdown sx={{ '& p': { m: 0, color: 'text.secondary' } }}>
                                {item.followup_question_text}
                              </MathMarkdown>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No follow-up question text available
                              </Typography>
                            )}
                            <MathMarkdown sx={{ '& p': { m: 0 } }}>
                              {`Follow-up answer: ${item.followup_student_answer || 'No follow-up answer recorded'}`}
                            </MathMarkdown>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight={700}>Student Summary</Typography>
          <Button variant="outlined" onClick={() => navigate(`/educator-portal/classes/${sectionId}${educatorQuerySuffix}`)}>
            Back to Assessment
          </Button>
        </Stack>

        {error && <Alert severity="error">Failed to load student profile</Alert>}

        {profile.show_incomplete_banner && (
          <Alert severity="warning">
            Assessment incomplete. Results reflect only completed questions.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{studentDisplay}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
              <Chip label={`Assessment status: ${profile.assessment_status === 'complete' ? 'Complete' : 'Incomplete'}`} />
              <Chip label={`Readiness: ${readinessLabel(profile.readiness_level)}`} />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6">Skills</Typography>
              <Tooltip title="Skills are grouped based on the student's responses in this assessment.">
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Stack>

            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Needs Review</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Student responses show limited or inconsistent success with this skill across assessed items.
                </Typography>
                {renderSkillAccordions(profile.skills.needs_review)}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Mixed Evidence</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Student responses show partial or uneven success with this skill, with correct work in some contexts but not others.
                </Typography>
                {renderSkillAccordions(profile.skills.mixed_evidence)}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Mastered</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Student responses show consistent success with this skill across assessed items.
                </Typography>
                {renderSkillAccordions(profile.skills.mastered)}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Evidence</Typography>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Missed questions</Typography>
            <List dense>
              {profile.evidence.missed_questions.map((item) => (
                <ListItem key={`${item.question_id}-${item.question_number}`}>
                  <ListItemText
                    primary={`Q${item.question_number}:`}
                    secondary={<MathMarkdown>{item.question_text}</MathMarkdown>}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
              {profile.evidence.missed_questions.length === 0 && (
                <ListItem><ListItemText secondary="No missed questions recorded." /></ListItem>
              )}
            </List>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Misconception tags</Typography>
            <List dense>
              {profile.evidence.misconception_tags.map((item, index) => (
                <ListItem key={`${item.question_id}-${index}`}>
                  <ListItemText
                    primary={`Q${item.question_number}: ${item.misconception_id || 'Unlabeled misconception'}`}
                    secondary={item.description || 'No description available'}
                  />
                </ListItem>
              ))}
              {profile.evidence.misconception_tags.length === 0 && (
                <ListItem><ListItemText secondary="No misconception tags recorded." /></ListItem>
              )}
            </List>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Follow-up question path</Typography>
            <List dense>
              {profile.evidence.followup_paths.map((item) => (
                <ListItem key={`${item.question_id}-${item.question_number}`}>
                  <ListItemText
                    primary={`Q${item.question_number}`}
                    secondary={JSON.stringify(item.followup_json)}
                  />
                </ListItem>
              ))}
              {profile.evidence.followup_paths.length === 0 && (
                <ListItem><ListItemText secondary="No follow-up path recorded." /></ListItem>
              )}
            </List>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Scratch work</Typography>
            <List dense>
              {profile.evidence.scratch_work.map((item) => (
                <ListItem key={item.attachment_id}>
                  <ListItemText
                    primary={item.file_name || item.attachment_id}
                    secondary={item.storage_uri || 'No storage URI available'}
                  />
                </ListItem>
              ))}
              {profile.evidence.scratch_work.length === 0 && (
                <ListItem><ListItemText secondary="No scratch work submitted." /></ListItem>
              )}
            </List>
          </CardContent>
        </Card> */}
      </Stack>
    </Box>
  );
}
